import { Resources } from '@berlitz/icons'
import get from 'lodash/get'
import * as React from 'react'
import { FormattedMessage } from 'react-intl'
import { Material as MaterialInterface, MaterialWithUnits } from '../../interfaces/Material'
import Select from '../Select'
import { ViewMaterialProp } from './interface'
import {
  ItemContainer,
  Label,
  LastMaterialsText,
  MaterialContainer,
  MaterialItemContainer,
  StyledButton,
  StyledCol,
  StyledLabel,
  StyledRow,
} from './style'
import getSuggestedMaterial, { getMaterial, getUnits, getUnit } from './utils'
import Input from '@components/Input'
import TextArea from '@berlitz/text-area'

const isOwnMaterial = (suggested: MaterialInterface) => suggested?.Material === 'Own Material'

const appendOwnMaterialOption = (
  suggested: MaterialInterface,
  materialOptions: MaterialWithUnits[],
  PrevMaterials: MaterialInterface[] = []
): MaterialWithUnits[] => {
  if (suggested.Material === 'Own Material') {
    const ownMaterial: MaterialWithUnits = {
      Chapter: suggested.Chapter,
      Id: suggested.Material,
      Name: suggested.Material,
      MaterialName: suggested.MaterialName,
      Page: `${suggested.Page}`,
      Point: `${suggested.Point}`,
      Type: suggested.Type,
      Units: [],
      Material: suggested.Material,
      Status: suggested.Status,
      URL: suggested.URL,
    }

    return materialOptions.concat(ownMaterial)
  } else {
    if (PrevMaterials.length > 0) {
      if (PrevMaterials[0]?.Material === 'Own Material') {
        const ownMaterial: MaterialWithUnits = {
          Chapter: PrevMaterials[0].Chapter,
          Id: PrevMaterials[0].Material,
          Name: PrevMaterials[0].Material,
          MaterialName: PrevMaterials[0].MaterialName,
          Page: `${PrevMaterials[0].Page}`,
          Point: `${PrevMaterials[0].Point}`,
          Type: PrevMaterials[0].Type,
          Units: [],
          Material: PrevMaterials[0].Material,
          Status: PrevMaterials[0].Status,
          URL: PrevMaterials[0].URL,
        }

        return materialOptions.concat(ownMaterial)
      }
    }
  }
  return materialOptions
}

const ViewMaterial: React.FC<ViewMaterialProp> = ({
  PrevMaterials = [],
  Materials = [],
  MaterialOptions = [],
  isFlex,
  isBoD1,
  refetch,
  activeMaterial,
  SessionInfoList,
}) => {
  const [stateMaterial, setStateMaterial] = React.useState('')
  const [stateChapter, setStateChapter] = React.useState('')
  const [prevMaterial, setPrevMaterial] = React.useState(PrevMaterials)
  const suggested = React.useMemo(
    () => getSuggestedMaterial(PrevMaterials, Materials, MaterialOptions, isFlex, activeMaterial, stateChapter),
    [PrevMaterials, activeMaterial]
  )

  React.useEffect(() => {
    setPrevMaterial(PrevMaterials)
  }, [PrevMaterials])

  React.useEffect(() => {
    setStateMaterial(suggested?.Material || '')
    if (isBoD1 && SessionInfoList?.[0]?.LPId) {
      const selectedChapter = SessionInfoList?.[0]?.UnitId ?? MaterialOptions?.[0]?.Chapter
      setStateChapter(selectedChapter)
    } else {
      setStateChapter(suggested?.Chapter || '')
    }
  }, [suggested])

  let materialOptions = MaterialOptions
  const customLesson = SessionInfoList?.[0]?.StudentNote
  if (!isBoD1) {
    materialOptions = appendOwnMaterialOption(suggested, materialOptions, PrevMaterials)
  } else if (isBoD1 && customLesson) {
    materialOptions = appendOwnMaterialOption(suggested, materialOptions, [])
  }

  const material = React.useMemo(() => getMaterial(stateMaterial || '', materialOptions), [stateMaterial])
  const units = isFlex ? material?.Units : getUnits(material?.Units)

  const unit = getUnit(stateChapter, get(material, 'Units', []))
  let chapterLabel = isFlex ? 'Live coaching session' : 'Chapter/Unit'
  if (isBoD1 && customLesson) {
    chapterLabel = 'Custom Lesson'
  } else if (isBoD1 && SessionInfoList?.[0]?.LPId) {
    chapterLabel = 'Topic'
  }

  return (
    <>
      {!isBoD1 && (
        <>
          <Label>
            <FormattedMessage id="Last used materials" />:
          </Label>
          <LastMaterialsText>
            {!prevMaterial.length ? (
              <FormattedMessage id="This is the first class of the program, no materials will be available until the lesson registration is submitted." />
            ) : (
              prevMaterial.map(({ ChapterName, Material, MaterialName, Page, Point }, index) => {
                const displayName = (
                  <>
                    {MaterialName} | {ChapterName && `${ChapterName}`}{' '}
                    {Page && (
                      <>
                        | <FormattedMessage id="Page" /> {Page}
                      </>
                    )}{' '}
                    {Point && `- ${Point}`}
                  </>
                )

                return (
                  <div key={`last-used-material-${Material}-${index}`}>
                    <span>{displayName}</span>
                  </div>
                )
              })
            )}
          </LastMaterialsText>
        </>
      )}

      {material && (
        <MaterialContainer>
          <StyledRow>
            <StyledCol xs={12}>
              <MaterialItemContainer>
                <StyledLabel>
                  <FormattedMessage id="Material" />:
                </StyledLabel>
                <Select
                  name="Material"
                  value={stateMaterial}
                  options={materialOptions.map((val) => ({
                    id: val.Id,
                    label: val.Name,
                    value: val.Id,
                  }))}
                  onChange={(event) => {
                    //setStateMaterial(event)
                    if (event === 'Own Material') {
                      setStateMaterial(event)
                      if (prevMaterial.length > 0 && prevMaterial[0].Material === 'Own Material') {
                        setStateChapter(prevMaterial[0].Chapter)
                      }
                    } else {
                      refetch(event)
                    }
                  }}
                  disableMargin={false}
                />
              </MaterialItemContainer>
            </StyledCol>

            {customLesson ? (
              <StyledCol xs={12}>
                <ItemContainer>
                  <StyledLabel>
                    <FormattedMessage id={chapterLabel} />:
                  </StyledLabel>
                  <TextArea name="StudentNote" value={customLesson} readonly />
                </ItemContainer>
              </StyledCol>
            ) : (
              <StyledCol xs={12}>
                <ItemContainer>
                  <StyledLabel>
                    <FormattedMessage id={chapterLabel} />:
                  </StyledLabel>
                  {isOwnMaterial(material) ? (
                    <Input name="Chapter" value={material.Chapter} disabled={true} />
                  ) : (
                    <Select
                      name="Chapter"
                      value={stateChapter}
                      options={(units || []).map((val) => ({
                        id: `${val.Id}`,
                        label: val.Name,
                        value: `${val.Id}`,
                      }))}
                      onChange={(event) => {
                        setStateChapter(event)
                      }}
                      disableMargin={false}
                    />
                  )}
                </ItemContainer>
              </StyledCol>
            )}

            <StyledCol xs={12}>
              <StyledButton
                icon={<Resources color="brand03" size="xs" />}
                iconPosition="left"
                narrow
                fullWidth
                disabled={!Boolean(get(unit, 'URL'))}
                onClick={() => {
                  if (unit && unit.URL) {
                    window.open(unit.URL, '_blank')
                  }
                }}
              >
                <FormattedMessage id="Open material" />
              </StyledButton>
            </StyledCol>
          </StyledRow>
        </MaterialContainer>
      )}
    </>
  )
}

export default ViewMaterial
